"use client";

import type {
  ChangeEvent,
  ReactNode,
} from "react";
import {
  Fragment,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ClipboardPaste,
  Paintbrush,
} from "lucide-react";
import {
  SITE_BUILDER_ICON_CATALOG,
  siteBuilderFieldDefault,
  siteBuilderFieldValue,
  type SiteBuilderBlock,
  type SiteBuilderIconCategory,
  type SiteBuilderInspectorField,
  withSiteBuilderFieldValue,
} from "@webdo/site-templates/builder";
import MediaImagePicker from "@/app/components/MediaImagePicker";
import { SidebarDisclosure } from "@/app/components/ui/SidebarDisclosure";
import type { MediaFile } from "@/app/modules/media/api";
import {
  BUILDER_BLOCK_STYLE_GROUPS,
  builderBlockStyleFieldsForContext,
  type BuilderBlockStyleFieldDefinition,
  type BuilderBlockStyleGroupDefinition,
} from "./blockInspectorStyleContract";
import {
  BuilderDimensionField,
  type BuilderDimensionConfig,
} from "./BuilderDimensionField";
import {
  BuilderBackgroundPicker,
  type BuilderBackgroundPickerLabels,
  type BuilderBackgroundPickerValue,
} from "./BuilderBackgroundPicker";
import {
  builderInspectorFieldIsVisible,
  builderInspectorPartOptionKey,
  withoutBuilderInspectorStyleConflicts,
  withBuilderInspectorAnimationMode,
} from "./builderInspectorMotion";
import {
  BUILDER_SPECIALIZED_INNER_BLOCK_TYPES,
  BuilderInnerBlockSpecializedPanels,
  type BuilderInnerBlockInspectorLabels,
} from "./BuilderInnerBlockSpecializedPanels";
import { BuilderIconLibrary } from "./BuilderIconLibrary";
import {
  BuilderInspectorSettingsSection,
} from "./BuilderInspectorSettingsSection";
import {
  builderMediaContentValue,
  builderMediaTargetForField,
  builderMediaUuidField,
  withBuilderMediaBlockContent,
} from "./builderMediaContent";
import {
  builderSelectOptions,
} from "./builderSelectOptions";
import type {
  BuilderStudioBlockInspectorContext,
  BuilderStudioBlockSelection,
} from "./types";

export type BuilderAdvancedBlockInspectorLabels = {
  backgroundPicker: BuilderBackgroundPickerLabels;
  blockLabel: (blockType: string) => string;
  chooseIcon: string;
  close: string;
  copyStyle: string;
  emptyIconLibrary: string;
  fieldLabel: (fieldKey: string) => string;
  groupLabel: (groupKey: string) => string;
  iconCategoryLabel: (category: SiteBuilderIconCategory) => string;
  iconItemLabel: (iconId: string) => string;
  iconLibraryTitle: string;
  iconSearch: string;
  innerBlocks: BuilderInnerBlockInspectorLabels;
  optionLabel: (fieldKey: string, value: string) => string;
  partLabel: (part: string) => string;
  pasteStyle: string;
  removeMedia?: string;
  reset: string;
  slotLabel: (slot: string) => string;
  styleClipboardDescription: string;
  styleClipboardReady: string;
  styleClipboardTitle: string;
};

type InspectorValueField = {
  defaultValue?: number;
  key: string;
  kind:
    | BuilderBlockStyleFieldDefinition["kind"]
    | SiteBuilderInspectorField["kind"];
  max?: number;
  min?: number;
  options?: readonly string[];
  scope: "content" | "options";
  step?: number;
  unit?: "" | "%" | "ms" | "px";
};

function inputValue(value: unknown): string | number {
  return typeof value === "string" || typeof value === "number"
    ? value
    : "";
}

function booleanValue(value: unknown): boolean {
  return value === true
    || value === 1
    || value === "1"
    || value === "true";
}

function fieldValue(
  block: SiteBuilderBlock,
  field: InspectorValueField,
  part: string | null,
): unknown {
  const key = field.scope === "options"
    ? builderInspectorPartOptionKey(part, field.key)
    : field.key;

  if (field.scope === "content" && field.kind === "media") {
    return builderMediaContentValue(
      block.content,
      builderMediaTargetForField(block.type, key),
    );
  }

  if (field.scope === "content") {
    return siteBuilderFieldValue(block, "content", key);
  }

  return part
    ? block.options?.[key]
    : siteBuilderFieldValue(block, "options", key);
}

function withFieldValue(
  block: SiteBuilderBlock,
  field: InspectorValueField,
  value: unknown,
  part: string | null,
): SiteBuilderBlock {
  const key = field.scope === "options"
    ? builderInspectorPartOptionKey(part, field.key)
    : field.key;

  if (field.scope === "content") {
    if (field.kind === "media") {
      return withBuilderMediaBlockContent(
        block,
        builderMediaTargetForField(block.type, key),
        typeof value === "string" ? value : "",
        null,
      );
    }

    return withSiteBuilderFieldValue(
      block,
      "content",
      key,
      value,
    );
  }

  let nextOptions = part
    ? { ...block.options }
    : {
        ...withSiteBuilderFieldValue(
          block,
          "options",
          key,
          value,
        ).options,
      };

  if (value === "") {
    delete nextOptions[key];
  } else {
    nextOptions[key] = value;
  }
  nextOptions = withoutBuilderInspectorStyleConflicts(
    nextOptions,
    field.key,
    value,
    part,
  );
  if (field.kind === "media") {
    delete nextOptions[builderMediaUuidField(key)];
  }

  return {
    ...block,
    options: Object.keys(nextOptions).length > 0
      ? nextOptions
      : undefined,
  };
}

function withMediaFieldValue(
  block: SiteBuilderBlock,
  field: InspectorValueField,
  url: string,
  mediaUuid: string,
  part: string | null,
): SiteBuilderBlock {
  if (field.scope === "content") {
    return withBuilderMediaBlockContent(
      block,
      builderMediaTargetForField(block.type, field.key),
      url,
      mediaUuid,
    );
  }

  const nextBlock = withFieldValue(block, field, url, part);
  const optionKey = builderInspectorPartOptionKey(part, field.key);

  return {
    ...nextBlock,
    options: {
      ...(nextBlock.options ?? {}),
      [builderMediaUuidField(optionKey)]: mediaUuid,
    },
  };
}

function InspectorPanel({
  children,
  groupKey,
  labels,
}: {
  children: ReactNode;
  groupKey: string;
  labels: BuilderAdvancedBlockInspectorLabels;
}) {
  return (
    <BuilderInspectorSettingsSection
      label={labels.groupLabel(groupKey)}
      legacyDataAttributes={{
        "data-builder-inspector-panel": groupKey,
      }}
      sectionKey={groupKey}
    >
      {children}
    </BuilderInspectorSettingsSection>
  );
}

function ResetButton({
  disabled,
  label,
  labels,
  onClick,
}: {
  disabled: boolean;
  label?: string;
  labels: BuilderAdvancedBlockInspectorLabels;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="rounded bg-surface px-2 py-1 text-[10px] font-semibold text-text transition-colors hover:bg-surface disabled:cursor-not-allowed disabled:opacity-35"
      disabled={disabled}
      onClick={onClick}
    >
      {label ?? labels.reset}
    </button>
  );
}

function AlignmentField({
  field,
  labels,
  onChange,
  value,
}: {
  field: InspectorValueField;
  labels: BuilderAdvancedBlockInspectorLabels;
  onChange: (value: string) => void;
  value: unknown;
}) {
  const options = field.options ?? ["left", "center", "right"];
  const selectedValue = typeof value === "string" ? value : "";

  return (
    <div
      className="grid grid-cols-[96px_minmax(0,1fr)] items-start gap-2 py-1.5 text-xs"
      data-builder-inspector-field={field.key}
    >
      <span className="min-w-0 pt-2 text-text">
        {labels.fieldLabel(field.key)}
      </span>
      <div
        className="grid min-w-0 gap-1.5"
        style={{
          gridTemplateColumns: `repeat(${Math.min(options.length, 4)}, minmax(0, 1fr))`,
        }}
      >
        {options.map((option) => (
          <button
            type="button"
            aria-pressed={selectedValue === option}
            className={[
              "grid min-h-12 min-w-0 place-items-center rounded-md border px-1 py-1.5 text-[9px] font-semibold transition-colors",
              selectedValue === option
                ? "border-primary bg-primary/15 text-primary"
                : "border-border bg-surface text-text-secondary hover:border-border-strong",
            ].join(" ")}
            key={option || "inherit"}
            onClick={() => onChange(option)}
          >
            <span aria-hidden="true" className="text-sm leading-none">
              {option === "center"
                ? "≡"
                : option === "right" || option === "bottom"
                  ? "☷"
                  : option === "left" || option === "top"
                    ? "☰"
                    : "∅"}
            </span>
            <span className="max-w-full truncate">
              {labels.optionLabel(field.key, option)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ColorField({
  field,
  labels,
  onChange,
  onPreview,
  value,
}: {
  field: InspectorValueField;
  labels: BuilderAdvancedBlockInspectorLabels;
  onChange: (value: string) => void;
  onPreview: (value: string) => void;
  value: unknown;
}) {
  const storedValue = typeof value === "string" ? value : "";
  const changedRef = useRef(false);
  const pickerValue = /^#[0-9a-f]{6}$/iu.test(storedValue)
    ? storedValue
    : "#000000";

  return (
    <div
      className="grid grid-cols-[96px_minmax(0,1fr)] items-center gap-2 py-1.5 text-xs"
      data-builder-inspector-field={field.key}
    >
      <span className="min-w-0 text-text">
        {labels.fieldLabel(field.key)}
      </span>
      <div className="flex min-w-0 items-center gap-1.5">
        <input
          type="color"
          aria-label={labels.fieldLabel(field.key)}
          className="h-8 w-9 shrink-0 cursor-pointer rounded border border-border bg-transparent p-1"
          value={pickerValue}
          onChange={(event) => {
            changedRef.current = true;
            onPreview(event.target.value);
          }}
          onBlur={(event) => {
            if (!changedRef.current) {
              return;
            }

            changedRef.current = false;
            onChange(event.target.value);
          }}
        />
        <input
          type="text"
          aria-label={labels.fieldLabel(field.key)}
          className="h-8 min-w-0 flex-1 rounded border border-border bg-surface px-2 text-xs text-text outline-none focus:border-primary"
          value={storedValue}
          onChange={(event) => {
            changedRef.current = true;
            onPreview(event.target.value);
          }}
          onBlur={(event) => {
            if (!changedRef.current) {
              return;
            }

            changedRef.current = false;
            onChange(event.target.value);
          }}
        />
        <ResetButton
          disabled={storedValue === ""}
          labels={labels}
          onClick={() => onChange("")}
        />
      </div>
    </div>
  );
}

function companionStyleField(
  key: "backgroundImage" | "textColor",
  scope: InspectorValueField["scope"],
): InspectorValueField {
  return {
    key,
    kind: key === "backgroundImage" ? "media" : "color",
    scope,
  };
}

function backgroundPickerValue(
  block: SiteBuilderBlock,
  backgroundField: InspectorValueField,
  part: string | null,
): BuilderBackgroundPickerValue {
  return {
    backgroundColor: String(
      fieldValue(block, backgroundField, part) ?? "",
    ),
    backgroundImage: String(
      fieldValue(
        block,
        companionStyleField("backgroundImage", backgroundField.scope),
        part,
      ) ?? "",
    ),
    textColor: String(
      fieldValue(
        block,
        companionStyleField("textColor", backgroundField.scope),
        part,
      ) ?? "",
    ),
  };
}

function withBackgroundPickerValue(
  block: SiteBuilderBlock,
  backgroundField: InspectorValueField,
  value: BuilderBackgroundPickerValue,
  part: string | null,
): SiteBuilderBlock {
  const withBackgroundColor = withFieldValue(
    block,
    backgroundField,
    value.backgroundColor,
    part,
  );
  const withBackgroundImage = withFieldValue(
    withBackgroundColor,
    companionStyleField("backgroundImage", backgroundField.scope),
    value.backgroundImage,
    part,
  );

  return withFieldValue(
    withBackgroundImage,
    companionStyleField("textColor", backgroundField.scope),
    value.textColor,
    part,
  );
}

function InspectorField({
  context,
  field,
  labels,
  onOpenIconLibrary,
  onOpenMedia,
  part,
}: {
  context: BuilderStudioBlockInspectorContext;
  field: InspectorValueField;
  labels: BuilderAdvancedBlockInspectorLabels;
  onOpenIconLibrary: (field: InspectorValueField) => void;
  onOpenMedia: (field: InspectorValueField) => void;
  part: string | null;
}) {
  const storedValue = fieldValue(context.block, field, part);
  const contextualDefault = part === null
    ? siteBuilderFieldDefault(
        context.block,
        context.definition,
        field.scope,
        field.key,
      )
    : undefined;
  const value = storedValue ?? contextualDefault;
  const previewValue = (nextValue: unknown) => {
    context.previewBlock(
      withFieldValue(context.block, field, nextValue, part),
    );
  };
  const commitValue = (nextValue: unknown) => {
    context.updateBlock(
      field.scope === "options" && field.key === "animationMode"
        ? withBuilderInspectorAnimationMode(
            context.block,
            typeof nextValue === "string" ? nextValue : "",
            part,
          )
        : withFieldValue(context.block, field, nextValue, part),
    );
  };
  const label = labels.fieldLabel(field.key);
  const iconField = field.scope === "content"
    && field.kind === "text"
    && field.key.toLocaleLowerCase().includes("icon");

  if (iconField) {
    const iconValue = typeof value === "string" ? value : "";
    const preview = SITE_BUILDER_ICON_CATALOG.find(
      (icon) => icon.value === iconValue,
    )?.preview ?? "◇";

    return (
      <div
        className="grid grid-cols-[96px_minmax(0,1fr)] items-center gap-2 py-1.5 text-xs"
        data-builder-inspector-field={field.key}
      >
        <span className="min-w-0 text-text">{label}</span>
        <button
          type="button"
          className="flex h-9 min-w-0 items-center gap-2 rounded border border-border bg-surface px-2 text-left text-xs text-text hover:border-primary"
          onClick={() => onOpenIconLibrary(field)}
        >
          <span className="grid h-6 w-6 shrink-0 place-items-center rounded bg-surface text-sm">
            {preview}
          </span>
          <span className="min-w-0 flex-1 truncate">
            {iconValue || labels.chooseIcon}
          </span>
        </button>
      </div>
    );
  }

  if (field.kind === "media") {
    return (
      <div
        className="grid grid-cols-[96px_minmax(0,1fr)] items-center gap-2 py-1.5 text-xs"
        data-builder-inspector-field={field.key}
      >
        <span className="min-w-0 text-text">{label}</span>
        <div className="flex min-w-0 gap-1.5">
          <input
            type="text"
            aria-label={label}
            className="h-8 min-w-0 flex-1 rounded border border-border bg-surface px-2 text-xs text-text outline-none focus:border-primary"
            value={inputValue(value)}
            onChange={(event) => previewValue(event.target.value)}
            onBlur={(event) => commitValue(event.target.value)}
          />
          <button
            type="button"
            className="rounded border border-border bg-surface px-2 text-[10px] font-semibold text-text hover:border-primary"
            onClick={() => onOpenMedia(field)}
          >
            {label}
          </button>
          <ResetButton
            disabled={inputValue(value) === ""}
            label={labels.removeMedia}
            labels={labels}
            onClick={() => commitValue("")}
          />
        </div>
      </div>
    );
  }

  if (field.kind === "color") {
    if (field.key === "backgroundColor") {
      const pickerValue = backgroundPickerValue(
        context.block,
        field,
        part,
      );

      return (
        <BuilderBackgroundPicker
          design={context.model.design}
          label={label}
          labels={labels.backgroundPicker}
          onCommit={(nextValue) => {
            context.updateBlock(
              withBackgroundPickerValue(
                context.block,
                field,
                nextValue,
                part,
              ),
            );
          }}
          onPreview={(nextValue) => {
            context.previewBlock(
              withBackgroundPickerValue(
                context.block,
                field,
                nextValue,
                part,
              ),
            );
          }}
          value={pickerValue}
        />
      );
    }

    return (
      <ColorField
        field={field}
        labels={labels}
        onChange={(nextValue) => commitValue(nextValue)}
        onPreview={(nextValue) => previewValue(nextValue)}
        value={value}
      />
    );
  }

  if (field.kind === "alignment") {
    return (
      <AlignmentField
        field={field}
        labels={labels}
        onChange={(nextValue) => commitValue(nextValue)}
        value={value}
      />
    );
  }

  if (field.kind === "dimension") {
    const config: BuilderDimensionConfig = {
      defaultValue: field.defaultValue ?? 0,
      max: field.max ?? 100,
      min: field.min ?? 0,
      step: field.step ?? 1,
      unit: field.unit ?? "px",
    };

    return (
      <BuilderDimensionField
        adjustLabel={label}
        config={config}
        label={label}
        onCommit={commitValue}
        onPreview={previewValue}
        onReset={() => commitValue("")}
        resetLabel={labels.reset}
        value={value}
      />
    );
  }

  if (field.kind === "toggle") {
    return (
      <label
        className="grid grid-cols-[96px_minmax(0,1fr)] items-center gap-2 py-1.5 text-xs"
        data-builder-inspector-field={field.key}
      >
        <span className="min-w-0 text-text">{label}</span>
        <input
          type="checkbox"
          checked={booleanValue(value)}
          className="h-4 w-4 rounded border-border bg-surface text-primary focus:ring-primary"
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            commitValue(event.target.checked);
          }}
        />
      </label>
    );
  }

  if (field.kind === "select") {
    const storedValue = String(inputValue(value));
    const options = builderSelectOptions(
      storedValue,
      field.options ?? [],
    );

    return (
      <label
        className="grid grid-cols-[96px_minmax(0,1fr)] items-center gap-2 py-1.5 text-xs"
        data-builder-inspector-field={field.key}
      >
        <span className="min-w-0 text-text">{label}</span>
        <select
          className="h-8 min-w-0 rounded border border-border bg-surface px-2 text-xs text-text outline-none focus:border-primary"
          disabled={options.length === 0}
          value={storedValue}
          onChange={(event) => commitValue(event.target.value)}
        >
          {options.map((option) => (
            <option key={option || "inherit"} value={option}>
              {labels.optionLabel(field.key, option)}
            </option>
          ))}
        </select>
      </label>
    );
  }

  const multiline = field.kind === "richText";
  const numeric = field.kind === "number";
  const dateTime = field.kind === "dateTime";

  return (
    <div
      className="grid grid-cols-[96px_minmax(0,1fr)] items-start gap-2 py-1.5 text-xs"
      data-builder-inspector-field={field.key}
    >
      <span className="min-w-0 pt-2 text-text">{label}</span>
      <div className="flex min-w-0 items-start gap-1.5">
        {multiline ? (
          <textarea
            aria-label={label}
            className="min-h-20 min-w-0 flex-1 resize-y rounded border border-border bg-surface px-2 py-2 text-xs text-text outline-none focus:border-primary"
            value={inputValue(value)}
            onChange={(event) => previewValue(event.target.value)}
            onBlur={(event) => commitValue(event.target.value)}
          />
        ) : (
          <input
            type={dateTime ? "datetime-local" : numeric ? "number" : "text"}
            aria-label={label}
            className="h-8 min-w-0 flex-1 rounded border border-border bg-surface px-2 text-xs text-text outline-none focus:border-primary"
            max={field.max}
            min={field.min}
            step={field.step}
            value={
              dateTime
                ? String(inputValue(value)).trim().replace(" ", "T").slice(0, 16)
                : inputValue(value)
            }
            onChange={(event) => {
              previewValue(
                numeric && event.target.value !== ""
                  ? event.target.valueAsNumber
                  : event.target.value,
              );
            }}
            onBlur={(event) => {
              commitValue(
                numeric && event.target.value !== ""
                  ? event.target.valueAsNumber
                  : event.target.value,
              );
            }}
          />
        )}
        <ResetButton
          disabled={inputValue(value) === ""}
          labels={labels}
          onClick={() => commitValue("")}
        />
      </div>
    </div>
  );
}

function styleFieldsForGroup(
  group: BuilderBlockStyleGroupDefinition,
  context: BuilderStudioBlockInspectorContext,
  part: string | null,
): InspectorValueField[] {
  return builderBlockStyleFieldsForContext(
    group,
    context.definition,
    part,
  ).map((field) => ({
    ...field,
    scope: "options" as const,
  }));
}

function definitionFields(
  fields: readonly SiteBuilderInspectorField[],
): InspectorValueField[] {
  const sharedFields = BUILDER_BLOCK_STYLE_GROUPS.flatMap(
    (group) => group.fields,
  );

  return fields.map((field) => {
    const sharedField = field.scope === "content"
      ? undefined
      : sharedFields.find((candidate) => candidate.key === field.key);

    return {
      defaultValue: sharedField?.defaultValue,
      key: field.key,
      kind: sharedField?.kind ?? field.kind,
      max: sharedField?.max,
      min: sharedField?.min,
      options: field.options ?? sharedField?.options,
      scope: field.scope === "content" ? "content" : "options",
      step: sharedField?.step,
      unit: sharedField?.unit,
    };
  });
}

function ScopeRow({
  active,
  children,
  depth,
  onSelect,
  title,
}: {
  active: boolean;
  children: ReactNode;
  depth: number;
  onSelect: () => void;
  title: string;
}) {
  const contentId = useId();
  const [retainedChildren, setRetainedChildren] = useState(children);

  if (active && retainedChildren !== children) {
    setRetainedChildren(children);
  }

  return (
    <section
      className="builder-inspector-scope"
    >
      <button
        type="button"
        aria-pressed={active}
        aria-expanded={active}
        aria-controls={contentId}
        className={[
          "builder-inspector-scope-trigger flex w-full items-center gap-2 px-2.5 py-2 text-left transition-colors hover:bg-surface-hover",
          active ? "text-primary" : "text-text",
        ].join(" ")}
        style={{ paddingLeft: `${10 + Math.min(depth, 6) * 12}px` }}
        onClick={onSelect}
      >
        <span aria-hidden="true" className="text-[10px] text-text-secondary">
          {active ? "▾" : "›"}
        </span>
        <span className="min-w-0 flex-1 truncate text-xs font-semibold">
          {title}
        </span>
      </button>
      <SidebarDisclosure id={contentId} open={active} unmountOnExit>
        {active ? children : retainedChildren}
      </SidebarDisclosure>
    </section>
  );
}

export function BuilderAdvancedBlockInspector({
  context,
  handledRootFieldKeys = [],
  labels,
  renderRootPanels,
  showDefinitionFields = true,
}: {
  context: BuilderStudioBlockInspectorContext;
  handledRootFieldKeys?: readonly string[];
  labels: BuilderAdvancedBlockInspectorLabels;
  renderRootPanels?: (
    context: BuilderStudioBlockInspectorContext,
  ) => ReactNode;
  showDefinitionFields?: boolean;
}) {
  const [iconField, setIconField] = useState<InspectorValueField | null>(null);
  const [mediaTarget, setMediaTarget] = useState<{
    field: InspectorValueField;
    part: string | null;
  } | null>(null);
  const handledIconRequestRef = useRef(0);
  const { iconLibraryRequestId, onIconLibraryRequestHandled } = context;
  const selectedPart = context.model.selectedBlock?.part ?? null;
  const canvasPart = selectedPart
    && selectedPart !== "root"
    && !selectedPart.startsWith("slot:")
      ? selectedPart
      : null;
  const rootFields = useMemo(
    () => definitionFields(context.definition.inspector),
    [context.definition.inspector],
  );
  const rootFieldKeys = new Set(
    rootFields.map((field) => `${field.scope}:${field.key}`),
  );
  const handledRootFields = new Set(handledRootFieldKeys);
  const specializedDefinitionKeys = new Set(
    context.block.type === "rating"
      ? ["ratingValue", "ratingMax"]
      : [],
  );
  const fallbackSelection: BuilderStudioBlockSelection =
    context.model.selectedBlock ?? {
      blockPath: null,
      blockType: context.block.type,
      blockUuid: context.block.uuid ?? null,
      part: canvasPart,
      rootIndex: 0,
      slot: null,
    };
  const scopeEntries = context.scopeEntries?.length
    ? context.scopeEntries
    : [{
      block: context.block,
      depth: 0,
      path: fallbackSelection.blockPath ?? "",
      selected: true,
      selection: fallbackSelection,
      slot: fallbackSelection.slot ?? null,
    }];
  const partScopes = [
    ...new Set([
      ...(context.partScopes ?? []),
      ...(canvasPart ? [canvasPart] : []),
    ]),
  ];
  const visibleRootFields = rootFields.filter((field) => (
    !specializedDefinitionKeys.has(field.key)
    && builderInspectorFieldIsVisible(
      context.block,
      field.key,
      null,
    )
  ));
  const hasSpecializedInnerPanel = BUILDER_SPECIALIZED_INNER_BLOCK_TYPES.some(
    (blockType) => blockType === context.block.type,
  );
  const orderedStyleGroups = [...BUILDER_BLOCK_STYLE_GROUPS].sort(
    (left, right) => {
      const order = [
        "style",
        "typography",
        "layout",
        "size",
        "spacing",
        "border",
        "effects",
        "motion",
        "layer",
      ];

      return order.indexOf(left.key) - order.indexOf(right.key);
    },
  );

  useEffect(() => {
    if (
      !iconLibraryRequestId
      || iconLibraryRequestId <= handledIconRequestRef.current
    ) {
      return;
    }

    const requestedIconField = rootFields.find((field) => (
      field.scope === "content"
      && field.kind === "text"
      && field.key.toLocaleLowerCase().includes("icon")
    )) ?? {
      key: "icon",
      kind: "text" as const,
      scope: "content" as const,
    };
    handledIconRequestRef.current = iconLibraryRequestId;
    setIconField(requestedIconField);
    onIconLibraryRequestHandled?.(iconLibraryRequestId);
  }, [
    iconLibraryRequestId,
    onIconLibraryRequestHandled,
    rootFields,
  ]);

  const renderPanels = (part: string | null) => (
    <>
      {context.styleClipboard ? (
        <section className="builder-inspector-clipboard">
          <p className="text-[13px] font-semibold text-text">
            {labels.styleClipboardTitle}
          </p>
          <p className="mt-1 text-xs leading-5 text-text-secondary">
            {context.styleClipboard.canPasteVisual
              ? labels.styleClipboardReady
              : labels.styleClipboardDescription}
          </p>
          <div className="mt-2 grid grid-cols-2 gap-1.5">
            <button
              type="button"
              className="flex h-9 items-center justify-center gap-1.5 rounded-md border border-border bg-surface px-2 text-[10px] font-semibold text-text hover:border-primary/45"
              data-builder-style-clipboard-action="copy-style"
              onClick={context.styleClipboard.copyVisual}
            >
              <Paintbrush aria-hidden="true" className="h-3.5 w-3.5" />
              <span className="truncate">{labels.copyStyle}</span>
            </button>
            <button
              type="button"
              className="flex h-9 items-center justify-center gap-1.5 rounded-md border border-border bg-surface px-2 text-[10px] font-semibold text-text hover:border-primary/45 disabled:opacity-35"
              data-builder-style-clipboard-action="paste-style"
              disabled={!context.styleClipboard.canPasteVisual}
              onClick={context.styleClipboard.pasteVisual}
            >
              <ClipboardPaste aria-hidden="true" className="h-3.5 w-3.5" />
              <span className="truncate">{labels.pasteStyle}</span>
            </button>
          </div>
        </section>
      ) : null}
      <div className="builder-inspector-settings-list">
        {part === null ? renderRootPanels?.(context) : null}
        {part === null
        && (
          hasSpecializedInnerPanel
          || (showDefinitionFields && visibleRootFields.length > 0)
        ) ? (
          <InspectorPanel
            groupKey="content"
            key={`content:${context.block.uuid ?? context.block.type}`}
            labels={labels}
          >
            {hasSpecializedInnerPanel ? (
              <BuilderInnerBlockSpecializedPanels
                context={context}
                labels={labels.innerBlocks}
              />
            ) : null}
            {showDefinitionFields ? visibleRootFields.map((field) => (
              <InspectorField
                context={context}
                field={field}
                key={`${field.scope}:${field.key}`}
                labels={labels}
                onOpenIconLibrary={setIconField}
                onOpenMedia={(field) => {
                  setMediaTarget({ field, part: null });
                }}
                part={null}
              />
            )) : null}
          </InspectorPanel>
        ) : null}
        {orderedStyleGroups.map((group) => {
          const fields = styleFieldsForGroup(group, context, part).filter(
            (field) => (
              builderInspectorFieldIsVisible(
                context.block,
                field.key,
                part,
              )
              && (
                part !== null
                || (
                  (
                    !showDefinitionFields
                    || !rootFieldKeys.has(`options:${field.key}`)
                  )
                  && !handledRootFields.has(field.key)
                )
              )
            ),
          );

          return fields.length > 0 ? (
            <InspectorPanel
              groupKey={group.key}
              key={`${context.block.uuid ?? context.block.type}:${part ?? "root"}:${group.key}`}
              labels={labels}
            >
              {fields.map((field) => (
                <InspectorField
                  context={context}
                  field={field}
                  key={`style:${part ?? "root"}:${field.key}`}
                  labels={labels}
                  onOpenIconLibrary={setIconField}
                  onOpenMedia={(nextField) => {
                    setMediaTarget({
                      field: nextField,
                      part,
                    });
                  }}
                  part={part}
                />
              ))}
            </InspectorPanel>
          ) : null;
        })}
      </div>
    </>
  );

  return (
    <>
      <div
        className="overflow-hidden bg-surface"
        data-builder-advanced-block-inspector={context.block.type}
        data-builder-block-inspector-part={canvasPart ?? "root"}
      >
        <div className="bg-surface">
          {scopeEntries.map((entry) => {
            const blockActive = entry.selected && canvasPart === null;

            return (
              <Fragment key={entry.path || `${entry.block.type}:${entry.depth}`}>
                <ScopeRow
                  active={blockActive}
                  depth={entry.depth}
                  onSelect={() => {
                    context.onSelectScope?.({
                      ...entry.selection,
                      part: null,
                    });
                  }}
                  title={labels.blockLabel(
                    entry.selected
                      ? context.definition.type
                      : entry.block.type,
                  )}
                >
                  {blockActive ? renderPanels(null) : null}
                </ScopeRow>
                {entry.selected ? partScopes.map((part) => {
                  const active = part === canvasPart;

                  return (
                    <ScopeRow
                      active={active}
                      depth={entry.depth + 1}
                      key={`${entry.path}:part:${part}`}
                      onSelect={() => {
                        context.onSelectScope?.({
                          ...entry.selection,
                          part,
                        });
                      }}
                      title={labels.partLabel(part)}
                    >
                      {active ? renderPanels(part) : null}
                    </ScopeRow>
                  );
                }) : null}
              </Fragment>
            );
          })}
        </div>
      </div>
      <BuilderIconLibrary
        labels={{
          categoryLabel: labels.iconCategoryLabel,
          close: labels.close,
          empty: labels.emptyIconLibrary,
          itemLabel: labels.iconItemLabel,
          search: labels.iconSearch,
          title: labels.iconLibraryTitle,
        }}
        onClose={() => setIconField(null)}
        onSelect={(nextValue) => {
          if (!iconField) {
            return;
          }

          context.updateBlock(
            withFieldValue(context.block, iconField, nextValue, null),
          );
        }}
        open={iconField !== null}
        value={iconField
          ? String(fieldValue(context.block, iconField, null) ?? "")
          : ""}
      />
      <MediaImagePicker
        multiple={false}
        publiclyAccessible
        onClose={() => setMediaTarget(null)}
        onSelect={(files: MediaFile[]) => {
          const file = files[0];

          if (!file || !mediaTarget) {
            return;
          }

          context.updateBlock(
            withMediaFieldValue(
              context.block,
              mediaTarget.field,
              file.url || file.thumbnailUrl || "",
              file.uuid,
              mediaTarget.part,
            ),
          );
        }}
        open={mediaTarget !== null}
      />
    </>
  );
}

export function renderBuilderAdvancedBlockInspector(
  context: BuilderStudioBlockInspectorContext,
  labels: BuilderAdvancedBlockInspectorLabels,
): ReactNode {
  return (
    <BuilderAdvancedBlockInspector
      context={context}
      labels={labels}
    />
  );
}
